'use client'

import * as React from 'react'
import {
  BACKGROUNDS, ACCENTS, getBackground, getAccent,
  type Background, type Accent,
} from '@/lib/copy/preview-themes'
import { FONT_PAIRS, type FontPair } from '@/lib/copy/styleguide-fonts'

// ── On-site Theme Picker ────────────────────────────────────────────────
// A per-browser PREVIEW tool to audition light paper colours + an accent
// (brand) colour + a font pairing against the real marketing pages, before we
// commit the winners as the canonical `.idukki` theme. OFF for normal
// visitors: only shows when the URL carries `?picker=1` (remembered per
// browser until closed). Overrides the main design tokens via inline custom
// properties on <html>, so it beats the forced `.idukki` class.
//
// Three independent axes: Background · Accent · Font. Legibility is automatic —
// the accent is used as a FILL (with auto black/white text) and, separately,
// as a darkened `--accent-ink` for emphasis TEXT on light, so a pale accent
// like lime is never washed-out text.

const LS_BG = 'idukki-preview-bg'
const LS_ACCENT = 'idukki-preview-accent'
const LS_FONT = 'idukki-preview-font'
const LS_ON = 'idukki-preview-on'

const WHITE = '#FFFFFF'
const NAVY = '#0B1120'

const BG_KEYS = [
  '--background', '--foreground', '--card', '--card-foreground',
  '--popover', '--popover-foreground', '--secondary', '--secondary-foreground',
  '--muted', '--muted-foreground', '--border', '--input',
] as const
const ACCENT_KEYS = [
  '--primary', '--primary-foreground', '--ring', '--accent',
  '--accent-foreground', '--accent-ink', '--brand-lime',
] as const
const FONT_KEYS = ['--font-sans', '--font-display', '--font-display-hero'] as const

function parseHex(hex: string): [number, number, number] {
  const m = hex.replace('#', '').match(/.{2}/g)
  if (!m) return [0, 0, 0]
  const [r, g, b] = m.slice(0, 3).map((s) => parseInt(s, 16))
  return [r, g, b]
}

function mix(a: string, b: string, t: number): [number, number, number] {
  const A = parseHex(a)
  const B = parseHex(b)
  return [0, 1, 2].map((i) => Math.round(A[i] + (B[i] - A[i]) * t)) as [number, number, number]
}

function toHsl([r, g, b]: [number, number, number]): { h: number; s: number; l: number } {
  const r1 = r / 255, g1 = g / 255, b1 = b / 255
  const max = Math.max(r1, g1, b1)
  const min = Math.min(r1, g1, b1)
  let h = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r1: h = (g1 - b1) / d + (g1 < b1 ? 6 : 0); break
      case g1: h = (b1 - r1) / d + 2; break
      default: h = (r1 - g1) / d + 4
    }
    h /= 6
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) }
}

const hslStr = ({ h, s, l }: { h: number; s: number; l: number }) => `${h} ${s}% ${l}%`
const rgbToTriplet = (rgb: [number, number, number]) => hslStr(toHsl(rgb))
const hexToTriplet = (hex: string) => hslStr(toHsl(parseHex(hex)))

// WCAG-ish relative luminance to decide black vs white text on a fill.
function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// A version of the accent that is always legible as TEXT on a light paper:
// keep hue + saturation, clamp lightness down so contrast holds.
function inkTriplet(hex: string): string {
  const { h, s } = toHsl(parseHex(hex))
  const { l } = toHsl(parseHex(hex))
  const targetL = Math.min(l, 34)
  return `${h} ${Math.max(s, 45)}% ${targetL}%`
}

function deriveBackground(b: Background): Record<string, string> {
  const bgHsl = hexToTriplet(b.bg)
  const inkHsl = hexToTriplet(b.ink)
  return {
    '--background': bgHsl,
    '--foreground': inkHsl,
    '--card': rgbToTriplet(mix(b.bg, WHITE, 0.5)),
    '--card-foreground': inkHsl,
    '--popover': rgbToTriplet(mix(b.bg, WHITE, 0.5)),
    '--popover-foreground': inkHsl,
    '--secondary': rgbToTriplet(mix(b.bg, b.ink, 0.07)),
    '--secondary-foreground': rgbToTriplet(mix(b.ink, b.bg, 0.12)),
    '--muted': rgbToTriplet(mix(b.bg, b.ink, 0.05)),
    '--muted-foreground': rgbToTriplet(mix(b.ink, b.bg, 0.45)),
    '--border': rgbToTriplet(mix(b.bg, b.ink, 0.16)),
    '--input': rgbToTriplet(mix(b.bg, b.ink, 0.16)),
  }
}

function deriveAccent(a: Accent): Record<string, string> {
  const fillFg = luminance(a.hex) > 0.55 ? NAVY : WHITE
  return {
    '--primary': hexToTriplet(a.hex),
    '--primary-foreground': hexToTriplet(fillFg),
    '--ring': inkTriplet(a.hex),
    '--accent': rgbToTriplet(mix(a.hex, WHITE, 0.84)),
    '--accent-foreground': inkTriplet(a.hex),
    '--accent-ink': inkTriplet(a.hex),
    // keep the CTA-fill brand token in sync so lime-fill components follow too
    '--brand-lime': hexToTriplet(a.hex),
  }
}

const loadedFontUrls = new Set<string>()
function setVars(map: Record<string, string>) {
  const s = document.documentElement.style
  for (const [k, v] of Object.entries(map)) s.setProperty(k, v)
}
function clearVars(keys: readonly string[]) {
  const s = document.documentElement.style
  for (const k of keys) s.removeProperty(k)
}
function applyFontVars(pair: FontPair) {
  if (pair.googleFontsUrl && !loadedFontUrls.has(pair.googleFontsUrl)) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = pair.googleFontsUrl
    document.head.appendChild(link)
    loadedFontUrls.add(pair.googleFontsUrl)
  }
  setVars({
    '--font-sans': pair.bodyFamily,
    '--font-display': pair.displayFamily,
    '--font-display-hero': pair.displayFamily,
  })
}

export function ThemePicker() {
  const [mounted, setMounted] = React.useState(false)
  const [enabled, setEnabled] = React.useState(false)
  const [open, setOpen] = React.useState(true)
  const [bgId, setBgId] = React.useState<string | null>(null)
  const [accentId, setAccentId] = React.useState<string | null>(null)
  const [fontId, setFontId] = React.useState<string | null>(null)

  React.useEffect(() => {
    setMounted(true)
    let on = false
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.has('picker')) {
        on = params.get('picker') !== '0'
        localStorage.setItem(LS_ON, on ? '1' : '0')
      } else {
        on = localStorage.getItem(LS_ON) === '1'
      }
    } catch {}
    setEnabled(on)
    if (!on) return

    try {
      const b = localStorage.getItem(LS_BG)
      if (b) { const bg = getBackground(b); if (bg) { setVars(deriveBackground(bg)); setBgId(bg.id) } }
      const a = localStorage.getItem(LS_ACCENT)
      if (a) { const ac = getAccent(a); if (ac) { setVars(deriveAccent(ac)); setAccentId(ac.id) } }
      const f = localStorage.getItem(LS_FONT)
      if (f) { const pair = FONT_PAIRS.find((p) => p.id === f); if (pair) { applyFontVars(pair); setFontId(pair.id) } }
    } catch {}
  }, [])

  const pickBg = React.useCallback((b: Background) => {
    setVars(deriveBackground(b)); setBgId(b.id)
    try { localStorage.setItem(LS_BG, b.id) } catch {}
  }, [])
  const pickAccent = React.useCallback((a: Accent) => {
    setVars(deriveAccent(a)); setAccentId(a.id)
    try { localStorage.setItem(LS_ACCENT, a.id) } catch {}
  }, [])
  const pickFont = React.useCallback((pair: FontPair | null) => {
    if (pair) { applyFontVars(pair); setFontId(pair.id); try { localStorage.setItem(LS_FONT, pair.id) } catch {} }
    else { clearVars(FONT_KEYS); setFontId(null); try { localStorage.removeItem(LS_FONT) } catch {} }
  }, [])

  const resetAll = React.useCallback(() => {
    clearVars(BG_KEYS); clearVars(ACCENT_KEYS); clearVars(FONT_KEYS)
    setBgId(null); setAccentId(null); setFontId(null)
    try { localStorage.removeItem(LS_BG); localStorage.removeItem(LS_ACCENT); localStorage.removeItem(LS_FONT) } catch {}
  }, [])
  const dismiss = React.useCallback(() => {
    resetAll(); setEnabled(false)
    try { localStorage.setItem(LS_ON, '0') } catch {}
  }, [resetAll])

  if (!mounted || !enabled) return null

  return (
    <div className="fixed bottom-4 left-4 z-[70] flex flex-col items-start gap-3">
      {open && (
        <div
          className="w-[340px] max-h-[78vh] overflow-auto rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-2xl shadow-black/25"
          role="dialog"
          aria-label="Theme preview picker"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Preview · this browser only
              </p>
              <h2 className="mt-1 font-display text-lg font-semibold tracking-tight">Theme picker</h2>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close picker and reset to brand default"
              className="rounded-full border border-border px-2.5 py-1 text-sm leading-none text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              ✕
            </button>
          </div>

          <SectionLabel>Background</SectionLabel>
          <ul className="grid grid-cols-4 gap-1.5">
            {BACKGROUNDS.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => pickBg(b)}
                  title={`${b.name} — ${b.note}`}
                  aria-pressed={bgId === b.id}
                  className={`group flex w-full flex-col items-center gap-1 rounded-lg border p-1 transition-transform duration-[var(--dur-fast)] [transition-timing-function:var(--ease-spring)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${bgId === b.id ? 'border-foreground ring-2 ring-foreground/30' : 'border-border'}`}
                >
                  <span className="h-8 w-full rounded-md border border-black/10" style={{ background: b.bg }} />
                  <span className="w-full truncate text-center text-[9px] font-medium leading-tight text-foreground">{b.name}</span>
                </button>
              </li>
            ))}
          </ul>

          <SectionLabel className="mt-4">Accent (CTA + highlights)</SectionLabel>
          <ul className="grid grid-cols-5 gap-1.5">
            {ACCENTS.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => pickAccent(a)}
                  title={a.name}
                  aria-pressed={accentId === a.id}
                  className={`flex w-full flex-col items-center gap-1 rounded-lg border p-1 transition-transform duration-[var(--dur-fast)] [transition-timing-function:var(--ease-spring)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${accentId === a.id ? 'border-foreground ring-2 ring-foreground/30' : 'border-border'}`}
                >
                  <span className="h-7 w-full rounded-md border border-black/10" style={{ background: a.hex }} />
                  <span className="w-full truncate text-center text-[9px] font-medium leading-tight text-foreground">{a.name}</span>
                </button>
              </li>
            ))}
          </ul>

          <SectionLabel className="mt-4">Font</SectionLabel>
          <ul className="space-y-1.5">
            <li>
              <FontRow label="Fraunces + Albert" hint="Brand default" active={fontId === null} onPick={() => pickFont(null)} />
            </li>
            {FONT_PAIRS.map((f) => (
              <li key={f.id}>
                <FontRow label={f.name} hint={f.vibe} family={f.displayFamily} active={fontId === f.id} onPick={() => pickFont(f)} />
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
            <button
              type="button"
              onClick={resetAll}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Reset to brand
            </button>
            <p className="text-right text-[10px] leading-tight text-muted-foreground">
              Nothing here ships.<br />Tell me the combo to lock it in.
            </p>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background shadow-lg shadow-black/20 transition-transform duration-[var(--dur-fast)] [transition-timing-function:var(--ease-spring)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-expanded={open}
      >
        <span className="flex gap-0.5" aria-hidden>
          <span className="h-3 w-3 rounded-full" style={{ background: '#CDEB45' }} />
          <span className="h-3 w-3 rounded-full" style={{ background: NAVY, outline: '1px solid rgba(255,255,255,0.25)' }} />
        </span>
        {open ? 'Hide themes' : 'Themes'}
      </button>
    </div>
  )
}

function SectionLabel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground ${className}`}>
      {children}
    </p>
  )
}

function FontRow({
  label, hint, family, active, onPick,
}: {
  label: string
  hint: string
  family?: string
  active: boolean
  onPick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left transition-colors duration-[var(--dur-fast)] hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? 'border-foreground ring-1 ring-foreground/30' : 'border-border'}`}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-foreground" style={family ? { fontFamily: family } : undefined}>
          {label}
        </span>
        <span className="block truncate text-[11px] text-muted-foreground">{hint}</span>
      </span>
      {active && <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-foreground">On</span>}
    </button>
  )
}
